import { useEffect, useRef, useState } from "react";
import { FormData, SetFormData } from "./FormData.tsx";

interface listingForm {
  formData: FormData;
  setFormData: SetFormData;
  setNextDisabled: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Step1Terms({
  formData,
  setFormData,
  setNextDisabled,
}: listingForm) {
  const termsRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const handleScroll = () => {
    if (termsRef.current && !hasScrolledToBottom) {
      const { scrollTop, scrollHeight, clientHeight } = termsRef.current;
      const bottom = scrollHeight - scrollTop <= clientHeight + 10; // 10px buffer
      if (bottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      acceptedTerms: e.target.checked,
    }));
  };

  useEffect(() => {
    const termsElement = termsRef.current;
    if (termsElement) {
      termsElement.addEventListener("scroll", handleScroll);
      return () => {
        termsElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [hasScrolledToBottom]);

  useEffect(() => {
    setNextDisabled(!formData.acceptedTerms);
  }, [formData.acceptedTerms]);

  return (
    <div className="">
      <h2 className="text-3xl font-bold mb-4">Terms & Conditions</h2>
      <hr className="border-t border-gray-500 my-4 w-full shadow-gray-400" />
      <div
        ref={termsRef}
        className="terms-content mb-6 p-4 border rounded max-h-[75vh] overflow-y-auto"
      >
        {/* Your existing terms content */}
        {/* 1. Introduction */}
        <div className="mb-4">
            <h3 className="font-bold mb-2">1. Introduction</h3>
            <p>
                These Terms and Conditions ("Agreement") govern your use of AirArrange, 
                a digital marketplace that enables workspace providers
                ("Hosts") and users ("Guests") to discover, book, and transact directly
                with each other.
            </p>
            <p className="mt-2">
                [Your Company Name] provides a technology platform only and does not act
                as a real estate agent, broker, property manager, or representative for
                any Host or Guest.
            </p>
        </div>


        {/* 2. Right to Lease/Sell Workspace */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">2. Right to Lease Workspace</h3>
          <p>
            By listing your Workspace on this Platform, you grant Us the{" "}
            <strong>non-exclusive right</strong> to:
          </p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Market, advertise, and lease your Workspace to potential tenants.
            </li>
            <li>
              Set lease terms (duration, pricing, and conditions) on your
              behalf, unless otherwise agreed in writing.
            </li>
            <li>
              Collect payments from tenants and disburse your share after
              deducting applicable fees (see Section 4).
            </li>
          </ul>
          You warrant that you own or have legal authority to lease the
          Workspace and that it complies with all local laws and regulations.
        </div>

        {/* 3. Listing Requirements */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">3. Listing Requirements</h3>
          <p className="">You agree to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Provide accurate descriptions, images, and availability of the
              Workspace.
            </li>
            <li>
              Disclose any restrictions (e.g., noise, accessibility) or
              additional fees (e.g., utilities).
            </li>
            <li>
              Maintain the Workspace in a safe, clean, and functional condition.
            </li>
          </ul>
          Misrepresentation may result in immediate removal from the Platform.
        </div>

        {/* 4. Fees & Payments */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">4. Fees & Payments</h3>
          <p className="">
            <strong>Commission:</strong> We retain [X]% of the total lease
            amount as a service fee. Payouts will be processed within [X]
            business days after tenant payment clears.
            <br />
            <strong>Taxes:</strong> You are responsible for any taxes related to
            your Workspace income. We may issue tax forms as required by law.
          </p>
        </div>

        {/* 5. Tenant Agreements */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">5. Tenant Agreements</h3>
          <p className="">
            Tenants will enter into a separate lease agreement with you (or Us,
            if authorized). You agree to honor the terms of any lease arranged
            through the Platform. Cancellations or modifications may incur
            penalties (see Section 7).
          </p>
        </div>

        {/* 6. Liability & Indemnification */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">6. Limitations of Liability</h3>
          <p className="">We are not liable for:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              Damages or losses arising from tenant use of your Workspace.
            </li>
            <li>
              Disputes between you and tenants (though we may mediate at our
              discretion).
            </li>
          </ul>
          You agree to indemnify Us against claims related to your Workspace or
          breach of this Agreement.
        </div>

        {/* 7. Termination */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">7. Termination</h3>
          <p className="">
            Either party may terminate this Agreement with [X] days' written
            notice. Active leases will continue until their expiration. Early
            termination fees may apply if you cancel mid-lease.
          </p>
        </div>

        {/* 8. Governing Law */}
        <div className="mb-4">
          <h3 className="font-bold mb-2">8. Governing Law</h3>
          <p className="">
            This Agreement is governed by the laws of [State/Country]. Disputes
            will be resolved in [Courts/Jurisdiction].
          </p>
        </div>

        {/* Bottom indicator (optional) */}
        {!hasScrolledToBottom && (
          <div className="text-center py-2 text-sm text-gray-500">
            Please scroll to the bottom to accept the terms
          </div>
        )}
      </div>

      <div className="flex items-center mb-6">
        <input
          type="checkbox"
          id="accept-terms"
          checked={formData.acceptedTerms}
          onChange={handleInputChange}
          disabled={!hasScrolledToBottom}
          className={`mr-2 ${
            !hasScrolledToBottom ? "opacity-50 cursor-not-allowed" : ""
          }`}
        />
        <label
          htmlFor="accept-terms"
          className={`text-sm ${!hasScrolledToBottom ? "text-gray-400" : ""}`}
        >
          I accept the terms and conditions
        </label>
      </div>
    </div>
  );
}
